import { z } from 'zod';
import { assertSafeIntegerInRange } from '../integers';

/**
 * The open world's grid: coordinates, bounds and the two versions that date
 * everything derived from them.
 *
 * ## A coordinate is a pair of indices, and a letter is a caption
 *
 * The world is persisted and reasoned about as `worldId + rowIndex +
 * columnIndex`, both zero-based. `A` is not a key, it is how row `0` is
 * PRINTED, and `F/10` is a label a player reads rather than anything a query
 * matches on. That separation is what makes the 26x26 expansion a
 * configuration change: the letters are a lookup into
 * {@link OPEN_WORLD_ROW_LETTERS}, and nothing downstream parses them.
 *
 * ## Why the active size is a constant and the maximum is a different one
 *
 * {@link OPEN_WORLD_ACTIVE_BOUNDS} is what exists today - ten by ten, one
 * hundred quadrants. {@link OPEN_WORLD_MAX_GRID_SIDE} is what the ARCHITECTURE
 * accepts - twenty-six, the length of the alphabet, which is the only thing
 * here that could not be widened without inventing a second presentation
 * scheme. Every function takes the bounds as an argument defaulting to the
 * active ones, so growing the world means creating a world with different
 * bounds, not rewriting neighbourhood or traversal.
 *
 * A world stores the bounds it was created with. A future world that is
 * fifteen by fifteen does not retroactively move the edges of this one, and
 * `F/10` keeps meaning what it meant.
 */

/**
 * The grid's shape version.
 *
 * Bumped when the meaning of a coordinate changes - a different origin, a
 * different letter order, a different reading of `rowIndex`. It is NOT bumped
 * when the world merely gets bigger: bounds are data on the world row.
 *
 * Deliberately its own version. The hunt, the duel, the cards and the jewels
 * have theirs, and nothing in this file may move any of them.
 */
export const OPEN_WORLD_GRID_VERSION = 1;

/**
 * The version of what the grid is FILLED with.
 *
 * Every catalogue entry is derived from `(worldId, grid version, content
 * version, secret seed)`, so bumping this re-rolls the whole world's content
 * while leaving the coordinates - and every player's memory of where things
 * were - intact. Separating it from {@link OPEN_WORLD_GRID_VERSION} is what
 * lets the content change without redefining what `C/5` means.
 *
 * ## Why it is 3
 *
 * Because **which identity each slot uses is derived differently** (gate
 * V28I.BW.5A). The old rule folded only the danger and the slot: the coordinate
 * never entered, so every quadrant of the same danger produced the same trio, and
 * seven of the twenty boss maps and twelve of the twenty mob maps were never
 * produced anywhere in the world — one of them boss **18**, a final. The new
 * rule permutes each band from the world's private seed and walks the
 * permutation with the quadrant's ordinal in the grid.
 *
 * That is content by the definition above: the same coordinate now names a
 * different enemy.
 *
 * ## What the stamp does NOT do, and why 3 is fail-closed
 *
 * A stamp changes the derivation LABEL; it does not change which function
 * derives. The rule is code, and code has one version at a time — so a world
 * still stamped 1 or 2 would be read by today’s rule under yesterday’s
 * label: its content would move under the feet of anybody who had learned it,
 * and its coverage was never verified, because the census did not exist when
 * it was born.
 *
 * Rather than reinterpret it in silence, such a row is REFUSED - see
 * {@link OPEN_WORLD_COVERAGE_CONTENT_VERSION}. Retiring a world is a decision
 * somebody takes, never a side effect of deploying.
 *
 * 2 was the comet returning after thirty minutes instead of an hour (gate
 * V28I.U); 1 was the world as it was first derived.
 */
export const OPEN_WORLD_CONTENT_VERSION = 3;

/**
 * **A primeira versão de conteúdo cuja cobertura foi verificada ao nascer.**
 *
 * Abaixo dela, um registo é de um mundo derivado por uma regra que já não
 * existe e cuja cobertura das vinte identidades nunca foi provada. Servi-lo
 * seria mostrar a um jogador um mundo diferente daquele que ele aprendeu, e
 * possivelmente um a que faltam chefes.
 *
 * A política é fechar: recusa-se, com diagnóstico. Não se re-semeia — trocar a
 * semente de um mundo que já tem salas e estado é apagar o mundo sem o dizer —
 * e não se reforma automaticamente, porque reformar é uma decisão.
 *
 * O mundo aberto ainda não foi implantado, portanto não se espera que exista
 * um registo assim. O caso fica escrito na mesma: a ausência de dados hoje não
 * é uma garantia, é uma coincidência.
 */
export const OPEN_WORLD_COVERAGE_CONTENT_VERSION = 3;

/**
 * The largest side the architecture accepts, per axis: twenty-six.
 *
 * Not a balance number and not a target - it is the length of the alphabet,
 * and therefore the point at which the PRESENTATION scheme would have to be
 * redesigned rather than merely re-parameterised. Everything else scales
 * freely below it.
 */
export const OPEN_WORLD_MAX_GRID_SIDE = 26;

/**
 * Row captions, in order. Index `0` prints as `A`.
 *
 * The one place letters exist. A consumer that wants to show a row asks
 * {@link openWorldRowLetter}; nothing else may hold a letter.
 */
export const OPEN_WORLD_ROW_LETTERS: readonly string[] = Object.freeze([
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
]);

/** Rows in the world as it exists today: `A` through `J`. */
export const OPEN_WORLD_ACTIVE_ROWS = 10;

/** Columns in the world as it exists today: `1` through `10`. */
export const OPEN_WORLD_ACTIVE_COLUMNS = 10;

/** How big a world is. Stored per world, never assumed from the constants. */
export interface OpenWorldBounds {
  readonly rows: number;
  readonly columns: number;
}

/**
 * Validates a pair of side lengths.
 *
 * Rejects rather than clamps, like every other whole-number input in this
 * repository: a world silently created one row short would produce a catalogue
 * that disagrees with every label a player has ever seen.
 */
export function openWorldBounds(rows: number, columns: number): OpenWorldBounds {
  assertSafeIntegerInRange(rows, 1, OPEN_WORLD_MAX_GRID_SIDE, 'open world rows');
  assertSafeIntegerInRange(columns, 1, OPEN_WORLD_MAX_GRID_SIDE, 'open world columns');
  return Object.freeze({ rows, columns });
}

/** The world that exists today: ten by ten, one hundred quadrants. */
export const OPEN_WORLD_ACTIVE_BOUNDS: OpenWorldBounds = openWorldBounds(
  OPEN_WORLD_ACTIVE_ROWS,
  OPEN_WORLD_ACTIVE_COLUMNS,
);

/**
 * One quadrant's address. Zero-based on both axes, and that is what persists.
 *
 * `{ rowIndex: 5, columnIndex: 9 }` is the quadrant a player calls `F/10`.
 */
export interface OpenWorldCoordinate {
  readonly rowIndex: number;
  readonly columnIndex: number;
}

export function isOpenWorldCoordinateWithin(
  coordinate: OpenWorldCoordinate,
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): boolean {
  return (
    Number.isSafeInteger(coordinate.rowIndex) &&
    Number.isSafeInteger(coordinate.columnIndex) &&
    coordinate.rowIndex >= 0 &&
    coordinate.rowIndex < bounds.rows &&
    coordinate.columnIndex >= 0 &&
    coordinate.columnIndex < bounds.columns
  );
}

/**
 * Builds a coordinate, or throws.
 *
 * The only constructor. A coordinate that exists is inside its world by
 * construction, which is what lets neighbourhood return `null` for "off the
 * edge" rather than for "malformed".
 */
export function openWorldCoordinate(
  rowIndex: number,
  columnIndex: number,
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): OpenWorldCoordinate {
  assertSafeIntegerInRange(rowIndex, 0, bounds.rows - 1, 'open world rowIndex');
  assertSafeIntegerInRange(columnIndex, 0, bounds.columns - 1, 'open world columnIndex');
  return Object.freeze({ rowIndex, columnIndex });
}

/** `0` prints as `A`. Throws above the alphabet rather than inventing a caption. */
export function openWorldRowLetter(rowIndex: number): string {
  assertSafeIntegerInRange(rowIndex, 0, OPEN_WORLD_MAX_GRID_SIDE - 1, 'open world rowIndex');
  const letter = OPEN_WORLD_ROW_LETTERS[rowIndex];
  if (letter === undefined) {
    throw new RangeError(`no row letter for index ${rowIndex}`);
  }
  return letter;
}

/** `0` prints as `1`. Columns are one-based on screen and zero-based in storage. */
export function openWorldColumnLabel(columnIndex: number): string {
  assertSafeIntegerInRange(columnIndex, 0, OPEN_WORLD_MAX_GRID_SIDE - 1, 'open world columnIndex');
  return String(columnIndex + 1);
}

/** The player-facing caption: `F/10`. Presentation only, never a key. */
export function openWorldCoordinateLabel(coordinate: OpenWorldCoordinate): string {
  const row = openWorldRowLetter(coordinate.rowIndex);
  return `${row}/${openWorldColumnLabel(coordinate.columnIndex)}`;
}

/**
 * Reads a caption back into indices.
 *
 * Exists for tooling and documentation - not for a request. Nothing on the
 * wire carries a label: the wire carries indices, because a label is a
 * rendering of them and parsing a rendering is how two sides end up
 * disagreeing about which quadrant they meant.
 */
export function parseOpenWorldCoordinateLabel(
  label: string,
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): OpenWorldCoordinate {
  const match = /^([A-Z])\/([1-9][0-9]*)$/.exec(label);
  const letter = match?.[1];
  const column = match?.[2];
  if (letter === undefined || column === undefined) {
    throw new RangeError('open world label must look like "F/10"');
  }
  return openWorldCoordinate(OPEN_WORLD_ROW_LETTERS.indexOf(letter), Number(column) - 1, bounds);
}

/**
 * A canonical string for a coordinate, for map keys and derivation labels.
 *
 * Indices and a separator, never the caption: the derivation that produces a
 * quadrant's content must not change the day somebody renames a row.
 */
export function openWorldCoordinateKey(coordinate: OpenWorldCoordinate): string {
  return `${coordinate.rowIndex}:${coordinate.columnIndex}`;
}

export function openWorldQuadrantCount(bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS): number {
  return bounds.rows * bounds.columns;
}

/**
 * Every coordinate in a world, row-major.
 *
 * The order is FIXED and part of the contract: a catalogue built by iterating
 * this is byte-identical wherever it is built. Nothing here iterates an
 * object's keys, which is the usual way a "deterministic" generator stops
 * being one.
 */
export function listOpenWorldCoordinates(
  bounds: OpenWorldBounds = OPEN_WORLD_ACTIVE_BOUNDS,
): readonly OpenWorldCoordinate[] {
  const coordinates: OpenWorldCoordinate[] = [];
  for (let rowIndex = 0; rowIndex < bounds.rows; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < bounds.columns; columnIndex += 1) {
      coordinates.push(openWorldCoordinate(rowIndex, columnIndex, bounds));
    }
  }
  return Object.freeze(coordinates);
}

/* -------------------------------------------------------------------------- */
/*  Wire                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A coordinate on the wire: two indices, `.strict()`.
 *
 * No label field, deliberately. A request that could send `"F/10"` would be a
 * request that could send `"F/11"`, and a response carrying both would let the
 * two disagree. The caption is computed where it is shown.
 */
export const openWorldCoordinateSchema = z
  .object({
    rowIndex: z
      .number()
      .int()
      .min(0)
      .max(OPEN_WORLD_MAX_GRID_SIDE - 1),
    columnIndex: z
      .number()
      .int()
      .min(0)
      .max(OPEN_WORLD_MAX_GRID_SIDE - 1),
  })
  .strict();

/** The active limits, as the server states them. Read-only for everyone else. */
export const openWorldBoundsSchema = z
  .object({
    rows: z.number().int().min(1).max(OPEN_WORLD_MAX_GRID_SIDE),
    columns: z.number().int().min(1).max(OPEN_WORLD_MAX_GRID_SIDE),
  })
  .strict();
